import {
  DEFAULT_INSURANCE_DISPLAY_MODE,
  INSURANCE_DISPLAY_MODES,
  type EmbedContext,
  type InsuranceDisplayMode,
} from '~/types/embed';

export function getInsuranceDisplayMode(embedSettings: unknown): InsuranceDisplayMode {
  if (embedSettings && typeof embedSettings === 'object') {
    const value = (embedSettings as Record<string, unknown>).insuranceDisplayMode;
    if (
      typeof value === 'string' &&
      (INSURANCE_DISPLAY_MODES as readonly string[]).includes(value)
    ) {
      return value as InsuranceDisplayMode;
    }
  }
  return DEFAULT_INSURANCE_DISPLAY_MODE;
}

export type KnownForStyle = 'pill' | 'colorful';
export const KNOWN_FOR_STYLES: readonly KnownForStyle[] = ['pill', 'colorful'];
export const DEFAULT_KNOWN_FOR_STYLE: KnownForStyle = 'pill';

export function isKnownForStyle(value: unknown): value is KnownForStyle {
  return typeof value === 'string' && (KNOWN_FOR_STYLES as readonly string[]).includes(value);
}

/** Mirrors the Contact Us (GD Web Form) `knownForStyle` setting for the specialist embed's
 * own "What we are known for!" section — see gd-web-form-settings.ts. */
export function getKnownForStyle(embedSettings: unknown): KnownForStyle {
  if (embedSettings && typeof embedSettings === 'object') {
    const value = (embedSettings as Record<string, unknown>).knownForStyle;
    if (isKnownForStyle(value)) return value;
  }
  return DEFAULT_KNOWN_FOR_STYLE;
}

type Carrier = NonNullable<EmbedContext['insuranceCarriers']>[number];

// `ALL` deliberately returns every carrier (including FEE_FOR_SERVICE).
// EmbeddedFormInfo currently renders only IN_NETWORK and OUT_OF_NETWORK
// sections, so FFS is dropped at render time today. Keeping the loader
// filter permissive lets a future FFS section light up without re-touching
// this helper.
export function filterInsuranceCarriersByDisplayMode<T extends Carrier>(
  carriers: readonly T[] | undefined,
  mode: InsuranceDisplayMode
): T[] {
  if (!carriers || carriers.length === 0) return [];
  switch (mode) {
    case 'HIDE':
      return [];
    case 'IN_NETWORK_ONLY':
      return carriers.filter(c => c.insuranceStatus === 'IN_NETWORK');
    case 'OUT_OF_NETWORK_ONLY':
      return carriers.filter(c => c.insuranceStatus === 'OUT_OF_NETWORK');
    case 'ALL':
      return [...carriers];
  }
}

// When multiple doctors at an office have different network statuses for the
// same carrier (e.g. Doctor A in-network with Delta, Doctor B out-of-network
// with Delta), the office-facing forms show that carrier as in-network only —
// never both. Pass one entry per doctor-carrier link (not pre-deduped); the
// first entry seen for a carrier id wins unless a later IN_NETWORK entry
// upgrades it (out-of-network never downgrades an existing in-network entry).
export function dedupeCarriersPreferringInNetwork<
  T extends { id: string; insuranceStatus: string },
>(entries: Iterable<T>): T[] {
  const map = new Map<string, T>();
  for (const entry of entries) {
    const existing = map.get(entry.id);
    if (!existing) {
      map.set(entry.id, entry);
    } else if (
      entry.insuranceStatus === 'IN_NETWORK' &&
      existing.insuranceStatus !== 'IN_NETWORK'
    ) {
      map.set(entry.id, { ...existing, insuranceStatus: 'IN_NETWORK' });
    }
  }
  return Array.from(map.values());
}

type FfsFallbackCarrier = { id: string; name: string; logoUrl: string };

// Shared by the Contact Us (GD Web Form) builder and public loaders: dedupe the real
// per-doctor carrier links (in-network wins — see dedupeCarriersPreferringInNetwork above),
// fall back to listing every company carrier as FEE_FOR_SERVICE only when there's no real
// per-doctor data at all, then apply the office's display-mode setting last — in that order,
// so "Don't show insurances" still hides everything even when there's no real data to have
// filtered. Extracted so this resolution can't silently drift between the two loaders the way
// the dedup logic itself once did (#1426/#1427) — both now call this instead of reimplementing it.
export function resolveDisplayedInsuranceCarriers(
  carrierLinks: readonly Carrier[],
  companyCarriers: readonly FfsFallbackCarrier[],
  mode: InsuranceDisplayMode
): Carrier[] {
  const deduped = dedupeCarriersPreferringInNetwork(carrierLinks);
  const resolved: Carrier[] =
    deduped.length > 0
      ? deduped
      : companyCarriers.map(c => ({ ...c, insuranceStatus: 'FEE_FOR_SERVICE' as const }));
  return filterInsuranceCarriersByDisplayMode(resolved, mode);
}

// Single source of truth for embed-settings defaults. #1087 made the
// truForm-style format the default: alternate layout, procedures wording,
// flat reason list on, adult chart toggle off.
const EMBED_SETTINGS_BOOLEAN_DEFAULTS = {
  showReferralStatus: true,
  showChiefComplaint: true,
  showAdultChartToggle: false,
  useProceduresLabel: true,
  flatReasonList: true,
  patientInsuranceOnLastStep: false,
  showMembershipPlansLink: false,
  alternateStepLayout: true,
  showDoctors: true,
  requireToothVerification: false,
  showMap: false,
  showInsuranceEmailOption: true,
  showInsuranceUploadOption: true,
} as const;

type EmbedSettingsBooleanKey = keyof typeof EMBED_SETTINGS_BOOLEAN_DEFAULTS;

export type ResolvedEmbedSettings = Record<EmbedSettingsBooleanKey, boolean> & {
  insuranceDisplayMode: InsuranceDisplayMode;
  knownForStyle: KnownForStyle;
  xrayUploadMessage?: string;
};

export function resolveEmbedSettings(raw: unknown): ResolvedEmbedSettings {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const resolved = { ...EMBED_SETTINGS_BOOLEAN_DEFAULTS } as Record<
    EmbedSettingsBooleanKey,
    boolean
  >;
  for (const key of Object.keys(EMBED_SETTINGS_BOOLEAN_DEFAULTS) as EmbedSettingsBooleanKey[]) {
    const value = source[key];
    if (typeof value === 'boolean') resolved[key] = value;
  }
  return {
    ...resolved,
    insuranceDisplayMode: getInsuranceDisplayMode(raw),
    knownForStyle: getKnownForStyle(raw),
    xrayUploadMessage:
      typeof source.xrayUploadMessage === 'string' && source.xrayUploadMessage
        ? source.xrayUploadMessage
        : undefined,
  };
}

export function getAllowedDoctorIds(raw: unknown): string[] {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const value = source.allowedDoctorIds;
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export function filterByAllowedDoctorIds<T extends { id: string }>(
  doctors: readonly T[],
  allowedDoctorIds: readonly string[]
): T[] {
  if (allowedDoctorIds.length === 0) return [...doctors];
  const allowed = new Set(allowedDoctorIds);
  return doctors.filter(d => allowed.has(d.id));
}

// An explicit, non-empty scope is authoritative — the Specialty & Doctor Routing checklist
// (embed builder) is exactly this: the admin deliberately choosing which specialties appear
// on the form, so a specialty they left unchecked must stay hidden even if some doctor's
// profile happens to carry it. Only falls back to the union of the office dentists'
// specialties (filled during onboarding) when there's no explicit scope at all — "never
// configured" still means "show everything" (#1376's original empty-allowlist case).
export function resolveEffectiveEmbedSpecialties(
  explicitScope: string[] | null | undefined,
  dentistSpecialties: Array<string[] | null | undefined>
): string[] | undefined {
  const explicit = new Set<string>();
  for (const specialty of explicitScope ?? []) {
    const trimmed = specialty?.trim();
    if (trimmed) explicit.add(trimmed);
  }
  if (explicit.size > 0) return Array.from(explicit);

  const fromDentists = new Set<string>();
  for (const specialties of dentistSpecialties) {
    for (const specialty of specialties ?? []) {
      const trimmed = specialty?.trim();
      if (trimmed) fromDentists.add(trimmed);
    }
  }
  return fromDentists.size > 0 ? Array.from(fromDentists) : undefined;
}
